import { styled, Box, Button } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ArchiveCampaignMutation,
  ArchiveCampaignMutationVariables,
  CampaignStatus,
  CopyCampaignMutation,
  CopyCampaignMutationVariables,
  IntegrationServiceType,
  UnarchiveCampaignMutation,
  UnarchiveCampaignMutationVariables,
} from "~frontend/__generated__/graphql";
import {
  COPY_CAMPAIGN,
  ARCHIVE_CAMPAIGN,
  UNARCHIVE_CAMPAIGN,
} from "~frontend/graphql/campaigns";
import { useIntegrations } from "~frontend/hooks/useIntegrations";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import {
  confirmMessageArchive,
  confirmMessageUnarchive,
} from "~frontend/util/campaignActionConfirmMessages";
import useMutation from "~frontend/util/useMutation";

import { SimulateDonationsDialog } from "./SimulateDonationsDialog";
import { CampaignForView } from "./types";

const ActionButtonWrapper = styled(Box)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
});

const ActionButton = styled(Button)({
  marginLeft: "12px",
  height: "27px",
});

export default function TopActionButtons({
  campaign,
  handleExport,
}: {
  campaign: CampaignForView;
  handleExport: () => void;
}): ReactElement {
  const { subaccountLink } = useSubaccount()!;
  const navigate = useNavigate();
  const confirm = useConfirm();
  const isStaff = useCurrentUserIsStaff();
  const [simulateDonationsDialogOpen, setSimulateDonationsDialogOpen] =
    useState(false);

  // Check for ACT_BLUE_WEBHOOK integration
  const { integrations } = useIntegrations({
    service: IntegrationServiceType.ActBlueWebhook,
  });
  const actBlueIntegration = integrations.find(
    integration => integration.service === IntegrationServiceType.ActBlueWebhook
  );

  const [copyCampaign] = useMutation<
    CopyCampaignMutation,
    CopyCampaignMutationVariables
  >(COPY_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not copy campaign",
    refetchQueries: ["listCampaigns"],
  });

  const [archiveCampaign] = useMutation<
    ArchiveCampaignMutation,
    ArchiveCampaignMutationVariables
  >(ARCHIVE_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not archive campaign",
    refetchQueries: ["listCampaigns"],
    awaitRefetchQueries: true,
  });

  const [unarchiveCampaign] = useMutation<
    UnarchiveCampaignMutation,
    UnarchiveCampaignMutationVariables
  >(UNARCHIVE_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not unarchive campaign",
    refetchQueries: ["listCampaigns"],
    awaitRefetchQueries: true,
  });

  async function handleCopy() {
    const { data, errors } = await copyCampaign({
      variables: {
        input: {
          campaignId: campaign.id,
        },
      },
    });

    if (!errors) {
      navigate(
        subaccountLink(`/campaigns/${data!.copyCampaign.shortId}/edit/basics`)
      )?.catch(console.error);
    }
  }

  async function handleArchive() {
    const { confirmed } = await confirm({
      title: (
        <>
          Archive <strong>{campaign.name}</strong>
        </>
      ),
      content: confirmMessageArchive(campaign),
    });
    if (!confirmed) {
      return;
    }

    await archiveCampaign({
      variables: { input: { campaignId: campaign.id } },
    });
  }

  async function handleUnarchive() {
    const { confirmed } = await confirm({
      title: "Unarchive Campaign",
      content: confirmMessageUnarchive(campaign),
    });
    if (!confirmed) {
      return;
    }

    await unarchiveCampaign({
      variables: { input: { campaignId: campaign.id } },
    });
  }

  // Determine if simulate donations button should be shown
  const shouldShowSimulateDonations =
    isStaff && // User must be staff
    import.meta.env.STW_ENV_NAME !== "production" && // Not in production
    campaign.status === CampaignStatus.InitialSendComplete && // Campaign has completed initial send
    actBlueIntegration; // Has active ACT_BLUE_WEBHOOK integration

  return (
    <>
      <ActionButtonWrapper>
        <ActionButton
          variant="contained"
          color="accessory"
          onClick={() =>
            navigate(
              subaccountLink(`/campaigns/${campaign.shortId}/edit/basics`)
            )
          }
        >
          Edit
        </ActionButton>
        {campaign.status === CampaignStatus.Archived ? (
          <ActionButton
            variant="contained"
            color="accessory"
            onClick={handleUnarchive}
          >
            Unarchive
          </ActionButton>
        ) : (
          <ActionButton
            variant="contained"
            color="accessory"
            onClick={handleArchive}
          >
            Archive
          </ActionButton>
        )}
        <ActionButton
          variant="contained"
          color="accessory"
          onClick={handleCopy}
        >
          Copy
        </ActionButton>
        <ActionButton
          variant="contained"
          color="accessory"
          onClick={handleExport}
        >
          Export
        </ActionButton>
        {shouldShowSimulateDonations && (
          <ActionButton
            variant="outlined"
            color="info"
            onClick={() => setSimulateDonationsDialogOpen(true)}
          >
            Simulate Donations
          </ActionButton>
        )}
      </ActionButtonWrapper>

      {shouldShowSimulateDonations && actBlueIntegration && (
        <SimulateDonationsDialog
          open={simulateDonationsDialogOpen}
          onClose={() => setSimulateDonationsDialogOpen(false)}
          campaignId={campaign.id}
          integrationId={actBlueIntegration.id}
        />
      )}
    </>
  );
}
